import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import 'react-phone-number-input/style.css';
import strings from '../../../utils/strings';
import API from '../../../utils/API';
import './UserModals.css';
import commonFunctions from '../../../utils/commonFunctions';

function RoleModal(props) {
  const {
    setResetRoleModalRef,
    roles,
    dataMap,
    loadTable,
    setSuccess,
    closeModal,
  } = props;

  //Implemented multi-language support
  const intl = useIntl();
  const translation = (id) => intl.formatMessage({ id });

  const [selectedRoles, setSelectedRoles] = useState([]);
  const [modalError, setModalError] = useState('');
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    setResetRoleModalRef(resetModal);
  }, [setResetRoleModalRef]);

  const resetModal = () => {
    setSelectedRoles([]);
    setModalError('');
    setSelectAll(false);
  };

  // Initialize modal when it's shown
  useEffect(() => {
    const modal = document.getElementById('setRolesModal');
    
    if (modal) {
      // Handle modal show event
      const handleModalShow = () => {
        // Reset state when modal is shown
        resetModal();
        
        // Get all selected user IDs
        const selectedUserIds = Object.keys(dataMap.selectedIds)
          .filter(id => dataMap.selectedIds[id] === 1);
        
        if (selectedUserIds.length > 0) {          
          // Get the first user's roles as the baseline
          const firstUserId = selectedUserIds[0];
          const firstUser = dataMap.data[firstUserId];
          
          if (firstUser && firstUser.roles && firstUser.roles.length > 0) {
            // Check if roles is an array of strings (names) or objects with IDs
            const isRolesArrayOfStrings = typeof firstUser.roles[0] === 'string';
            
            // Get role IDs or names from first user's roles
            const firstUserRoles = isRolesArrayOfStrings 
              ? firstUser.roles // Already role names
              : firstUser.roles.map(role => role.id); // Extract role IDs from objects
              
            
            // For multiple users, check if all selected users have the same roles
            let allUsersHaveSameRoles = true;
            
            // Skip the first user since we already processed it
            if (selectedUserIds.length > 1) {
              for (let i = 1; i < selectedUserIds.length; i++) {
                const userId = selectedUserIds[i];
                const user = dataMap.data[userId];
                
                if (!user || !user.roles) {
                  allUsersHaveSameRoles = false;
                  break;
                }
                
                // Get current user's roles (either names or IDs)
                const userRoles = isRolesArrayOfStrings
                  ? user.roles
                  : user.roles.map(role => role.id);
                
                // Check if the roles match the first user's roles
                if (userRoles.length !== firstUserRoles.length || 
                    !userRoles.every(role => firstUserRoles.includes(role))) {
                  allUsersHaveSameRoles = false;
                  break;
                }
              }
            }
            
            // Only proceed with pre-selection if it's a single user or all users have the same roles
            if (selectedUserIds.length === 1 || allUsersHaveSameRoles) {

              // If roles are strings (names), map them to IDs using the roles array
              if (isRolesArrayOfStrings) {
                const roleIds = [];
                // Find matching role IDs for each role name
                // Only consider role names that are available in the current context (Message Manager roles)
                firstUserRoles.forEach(roleName => {
                  // Use case-insensitive comparison to handle potential case differences
                  const matchingRole = roles.find(r => r.name.toLowerCase() === roleName.toLowerCase());
                  if (matchingRole) {
                    roleIds.push(matchingRole.id);
                  }
                });
                setSelectedRoles(roleIds);
              } else {
                // If roles are objects with IDs, we need to filter them to only include Message Manager roles
                // Get the available role IDs in the current context
                const availableRoleIds = roles.map(role => role.id);
                
                // Filter the user's roles to only include Message Manager roles
                const filteredRoleIds = firstUserRoles.filter(roleId => {
                  return availableRoleIds.includes(roleId);
                });
                
                setSelectedRoles(filteredRoleIds);
              }
              
              // Check if all roles are selected to set selectAll state
              if (firstUserRoles.length === roles.length) {
                setSelectAll(true);
              }
            }
          }
        }
      };
      
      // Add event listeners for modal show and hidden events
      modal.addEventListener('show.bs.modal', handleModalShow);
      
      // Clean up event listeners
      return () => {
        modal.removeEventListener('show.bs.modal', handleModalShow);
      };
    }
  }, [dataMap, roles]);

  function setSelectedRole(e) {
    let newArray = [...selectedRoles];
    if (newArray.includes(e.target.value)) {
      let index = newArray.indexOf(e.target.value);
      if (index !== -1) {
        newArray.splice(index, 1);
      }
    } else {
      newArray.push(e.target.value);
    }
    setSelectedRoles(newArray);
    console.log('Updated selected roles:', newArray);
  }

  function handleSelectAll(e) {
    setSelectAll(e.target.checked);
    if (e.target.checked) {
      setSelectedRoles(roles.map(role => role.id));
    } else {
      setSelectedRoles([]);
    }
  }

  const handleAssignRoles = async (e) => {
    const selectedUserIds = Object.keys(dataMap.selectedIds)
      .filter(id => dataMap.selectedIds[id] === 1);

    if (selectedUserIds.length === 0) {
      setModalError('NO_USERS_SELECTED');
      return;
    }
    //Bug #19054 Cannot De-assign Roles or Sites from users.
    /*if (selectedRoles.length === 0) {
      setModalError(<FormattedMessage id="NO_ROLES_SELECTED" />);
      return;
    }*/

    API.setRoles(selectedUserIds, selectedRoles, "sbmm")
      .then((res) => {
        closeModal("setRolesModal");
        resetModal();
        loadTable(dataMap);
        setSuccess(<FormattedMessage id="EDIT_USER_ROLES_SUCCESS" />);
      })
      .catch((err) => {
        console.error(`Failed to edit user-roles: ${err}`);
        setModalError(<FormattedMessage id="EDIT_USER_ROLES_FAILED" values={{ errorMsg: <b>{err.message}</b> }} />);
      });

    e.preventDefault();
  };

  return (
    <div className="modal modal-mse fade" id="setRolesModal" tabIndex="-1" aria-labelledby="roleModalLabel" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content modal-mes-content">
          <div className="modal-header modal-mes-header" style={{ backgroundColor: '#347BFA' }}>
            <h5 className="modal-title modal-mes-title"><FormattedMessage id="ASSIGN_ROLES" /></h5>
            <button id="closeRolesModal" type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div className="modal-body">
            <div className="card" style={{ borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ overflowX: 'hidden' }}>
                <table className="table">
                  <thead>
                    <tr style={{ backgroundColor: '#E9F3FC' }}>
                      <th className="mesCustomHeaderCell" scope="col">
                        <input
                          id="selectAll"
                          type="checkbox"
                          className="form-check-input"
                          checked={selectAll}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th className="mesCustomHeaderCell" scope="col"><FormattedMessage id="FEATURE" /></th>
                      <th className="mesCustomHeaderCell" scope="col"><FormattedMessage id="ROLE" /></th>
                      <th className="mesCustomHeaderCell" scope="col"><FormattedMessage id="ROLE_DESCRIPTION" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {/*roles*/commonFunctions.getRolesSort(roles).map((role) => {
                      return (
                        <tr key={role.id}>
                          <th className="mesCustomBodyCell" scope="row">
                            <input
                              id="roleCheckbox"
                              className="form-check-input"
                              type="checkbox"
                              value={role.id}
                              checked={selectedRoles.includes(role.id)}
                              onChange={(e) => { setSelectedRole(e); }}
                            />
                          </th>                        
                          <td className="mesCustomBodyCell" >
                            {
                              (role.features.length > 0) ?
                              role.features.map((feature) => { return translation(`${feature.id}-FEATURENAME`); }).join(', ') :
                              translation('672597bc-8086-4457-b036-690c985ef829-FEATURENAME')
                            }                          
                          </td>
                          <td className="mesCustomBodyCell"><FormattedMessage id={`${role.id}-ROLENAME`}/></td>                      
                          <td className="mesCustomBodyCell roleDescription" ><FormattedMessage id={`${role.id}-DESCRIPTION`}/></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            {modalError && (
              <div className="modal-error invalid-feedback d-block fw-bold text-center">
                {modalError}
              </div>
            )}
          </div>
          <div className="modal-footer modal-mes-footer d-flex align-items-center">
            <button id="backRoleModalBtn" className="btn btn-outline-secondary me-3" type="button" data-bs-dismiss="modal"><FormattedMessage id="BACK" /></button>
            <button id="saveRolesBtn" className="btn btn-primary but-darkblue" type="button" onClick={handleAssignRoles}><FormattedMessage id="SAVE" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RoleModal;